import {
  BRANCH_NO_OFFSET,
  BRANCH_YES_OFFSET,
  DEFAULT_START_X,
  DEFAULT_START_Y,
  EVENT_X,
  EVENT_Y,
  getShapeType,
  PRIMITIVE_GAP,
  PRIMITIVE_WIDTH,
} from "../../types/editor.constants";
import {
  Primitive,
  Step,
  StepConnection,
} from "../../types/TransactionTemplate.types";

interface TransactionAction {
  type:
    | "primitive"
    | "for"
    | "if"
    | "function"
    | "return"
    | "break"
    | "throw"
    | "subtransaction";
  link?: string;
  func?: string;
  params?: string[];
  funcParams?: string[];
  elems?: string;
  start?: string;
  id?: string;
  transactionId?: number;
  cond?: string;
  bodyLine?: TransactionAction[];
  yesLine?: TransactionAction[];
  noLine?: TransactionAction[];
}

interface Transaction {
  id: number;
  actions: TransactionAction[] | Record<string, any>;
  queues: any;
}

interface ProcessTemplate {
  id: number;
  name: string;
  template?: { steps?: any[] };
}

const getActionVisualType = (action: TransactionAction): Primitive["type"] => {
  const visualMap: Record<string, Primitive["type"]> = {
    if: "condition",
    for: "loop",
    function: "function",
    subtransaction: "subtransaction",
    return: "return",
    throw: "throw",
    break: "break",
    primitive: "primitive",
  };

  return visualMap[action.type] || "primitive";
};

const getActionName = (action: TransactionAction): string => {
  switch (action.type) {
    case "primitive":
      return `${action.link ? `${action.link} = ` : ""} ${action.func || ""}`.trim();
    case "return":
      return `return ${action.func || ""}`;
    case "throw":
      return `throw`;
    case "function":
      return `${action.link ? `${action.link} = ` : ""} ${action.func || ""}(...)`;
    case "subtransaction":
      return `${action.link ? `${action.link} = ` : ""} sub(${action.transactionId})`;
    case "for":
      return `for ${action.id || ""} in ${action.elems || ""}`;
    case "if":
      return `if ${action.cond || ""}`;
    default:
      return action.type;
  }
};

export function convertTransactionToSteps(transaction: Transaction): {
  steps: Step[];
  stepConnections: StepConnection[];
} {
  const newSteps: Step[] = [];
  const stepIndex = 0;
  let primitiveCounter = 0;

  const stepId = `step-${stepIndex}`;
  const step: Step = {
    id: stepId,
    name: `Transaction ${transaction.id}`,
    event: {
      id: `event-${stepIndex}`,
      name: "transaction_start",
      x: EVENT_X,
      y: EVENT_Y,
      data: {},
      from: [],
    },
    primitives: [],
    connections: [],
    y: 50,
    originalData: transaction,
    outgoingStepConnections: [],
  };
  newSteps.push(step);

  const processActions = (
    actions: TransactionAction[],
    startX: number,
    startY: number,
  ): { maxX: number; maxY: number; lastPrimId: string | null } => {
    let currentX = startX;
    const currentY = startY;
    let maxX = startX;
    let maxY = startY;
    let lastPrimId: string | null = null;

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const primId = `prim-${stepIndex}-${primitiveCounter++}`;
      const primType = getActionVisualType(action);
      const primName = getActionName(action);

      const primitive: Primitive = {
        id: primId,
        name: primName,
        x: currentX,
        y: currentY,
        type: primType,
        data: action,
        color: "#000000",
      };

      step.primitives.push(primitive);

      if (lastPrimId) {
        step.connections.push({
          from: lastPrimId,
          to: primId,
          type: "primitive",
        });
      }

      lastPrimId = primId;
      maxX = Math.max(maxX, currentX + PRIMITIVE_GAP);
      maxY = Math.max(maxY, currentY + 80);

      if (action.type === "if") {
        let branchMaxX = currentX + PRIMITIVE_GAP;
        let lowestYReached = currentY;

        // YES branch
        if (action.yesLine?.length) {
          const yesY = currentY + BRANCH_YES_OFFSET;
          const yesResult = processActions(
            action.yesLine,
            currentX + PRIMITIVE_GAP,
            yesY,
          );
          const yesPrim = step.primitives.find(
            (p) => p.x === currentX + PRIMITIVE_GAP && p.y === yesY,
          );
          if (yesPrim) {
            step.connections.push({
              from: primId,
              to: yesPrim.id,
              type: "primitive",
              branch: "yes",
            });
          }
          branchMaxX = Math.max(branchMaxX, yesResult.maxX);
          maxX = Math.max(maxX, yesResult.maxX);
          maxY = Math.max(maxY, yesResult.maxY);
          lowestYReached = yesResult.maxY;
        }

        // NO branch
        if (action.noLine?.length) {
          let noY = currentY + BRANCH_NO_OFFSET;
          if (lowestYReached > currentY) {
            const verticalGap = 40;
            noY = Math.max(noY, lowestYReached + verticalGap);
          }

          const noResult = processActions(
            action.noLine,
            currentX + PRIMITIVE_GAP,
            noY,
          );
          const noPrim = step.primitives.find(
            (p) => p.x === currentX + PRIMITIVE_GAP && p.y === noY,
          );
          if (noPrim) {
            step.connections.push({
              from: primId,
              to: noPrim.id,
              type: "primitive",
              branch: "no",
            });
          }
          branchMaxX = Math.max(branchMaxX, noResult.maxX);
          maxX = Math.max(maxX, noResult.maxX);
          maxY = Math.max(maxY, noResult.maxY);
        }

        currentX = branchMaxX + PRIMITIVE_WIDTH;
        lastPrimId = i < actions.length - 1 ? primId : null;
      } else if (action.type === "for" && action.bodyLine?.length) {
        const bodyY = currentY + BRANCH_YES_OFFSET;
        const bodyResult = processActions(
          action.bodyLine,
          currentX + PRIMITIVE_GAP,
          bodyY,
        );
        const bodyPrim = step.primitives.find(
          (p) => p.x === currentX + PRIMITIVE_GAP && p.y === bodyY,
        );
        if (bodyPrim) {
          step.connections.push({
            from: primId,
            to: bodyPrim.id,
            type: "primitive",
          });
        }
        currentX = bodyResult.maxX;
        maxX = Math.max(maxX, bodyResult.maxX);
        maxY = Math.max(maxY, bodyResult.maxY);
      } else {
        currentX += PRIMITIVE_GAP;
      }
    }

    return { maxX, maxY, lastPrimId };
  };

  if (transaction.actions) {
    const actionsArray: TransactionAction[] = Array.isArray(transaction.actions)
      ? transaction.actions
      : Object.values(transaction.actions);

    if (actionsArray.length > 0) {
      processActions(actionsArray, DEFAULT_START_X, DEFAULT_START_Y);
    }
  }

  if (step.event && step.primitives.length > 0) {
    step.connections.unshift({
      from: step.event.id,
      to: step.primitives[0].id,
      type: "primitive",
    });
  }

  return { steps: newSteps, stepConnections: [] };
}

export function convertTemplateToSteps(templateData: ProcessTemplate): {
  steps: Step[];
  stepConnections: StepConnection[];
} {
  const newSteps: Step[] = [];
  const newStepConnections: StepConnection[] = [];

  if (!templateData.template?.steps) {
    return { steps: [], stepConnections: [] };
  }

  templateData.template.steps.forEach((stepData: any, stepIndex: number) => {
    const step: Step = {
      id: `step-${stepIndex}`,
      name: `step${stepIndex + 1}`,
      event: null,
      primitives: [],
      connections: [],
      y: stepIndex * 300 + 50,
      originalData: stepData,
      outgoingStepConnections: [],
    };

    if (stepData.event) {
      step.event = {
        id: `event-${stepIndex}`,
        name: stepData.event,
        x: EVENT_X,
        y: EVENT_Y,
        data: stepData,
        from: stepData.from || [],
      };

      (stepData.from || []).forEach((fromStepName: string) => {
        const fromIdx = parseInt(fromStepName.replace("step", "")) - 1;
        if (fromIdx >= 0) {
          newStepConnections.push({
            from: `step-${fromIdx}`,
            to: step.id,
            toEvent: true,
          });
        }
      });
    }

    if (Array.isArray(stepData.primitives)) {
      stepData.primitives.forEach((primData: any, primIndex: number) => {
        const primName = primData.primitive || primData.process || "unknown";
        let primType: Primitive["type"] =
          primData.type === "process" ? "process" : getShapeType(primName);
        if (primData.process) primType = "process";

        step.primitives.push({
          id: `prim-${stepIndex}-${primIndex}`,
          name: primName,
          x: DEFAULT_START_X + primIndex * PRIMITIVE_GAP,
          y: EVENT_Y,
          type: primType,
          data: primData,
        });
      });
    }

    if (Array.isArray(stepData.connections)) {
      stepData.connections.forEach((conn: any) => {
        if (conn.toStep !== undefined) {
          newStepConnections.push({
            from: step.id,
            to: `step-${conn.toStep}`,
            fromPrimitive: conn.from,
            toEvent: true,
          });
        } else {
          step.connections.push({
            from: conn.from,
            to: conn.to,
            type: "primitive",
          });
        }
      });
    } else {
      if (step.event && step.primitives.length > 0) {
        step.connections.push({
          from: step.event.id,
          to: step.primitives[0].id,
          type: "primitive",
        });
      }
      for (let i = 1; i < step.primitives.length; i++) {
        step.connections.push({
          from: step.primitives[i - 1].id,
          to: step.primitives[i].id,
          type: "primitive",
        });
      }
    }

    newSteps.push(step);
  });

  return { steps: newSteps, stepConnections: newStepConnections };
}

export function buildActionTree(
  step: Step,
  originalPositions?: Map<string, { x: number; y: number }>,
): TransactionAction[] {
  if (!step.primitives.length) return [];

  const actions: TransactionAction[] = [];
  const visited = new Set<string>();

  function processChain(
    id: string | null,
    list: TransactionAction[],
    stopAt: Set<string>,
  ): void {
    while (id) {
      if (!id || stopAt.has(id) || visited.has(id)) break;
      const prim = step.primitives.find((p) => p.id === id);
      if (!prim) break;
      visited.add(id);

      let action: TransactionAction;
      if (prim.type === "condition") {
        action = {
          type: "if",
          cond: prim.data?.cond ?? "",
          yesLine: [],
          noLine: [],
        };
      } else if (prim.type === "loop") {
        action = { ...prim.data, type: "for", bodyLine: [] };
      } else {
        action = { type: prim.data?.type || "primitive" } as TransactionAction;
        const skip = new Set(["_isNew", "primitive", "process"]);
        for (const [k, v] of Object.entries(prim.data || {})) {
          if (!skip.has(k) && isNaN(Number(k))) (action as any)[k] = v;
        }
      }
      list.push(action);

      if (prim.type === "condition") {
        const merge = findMergeByConnections(step, id);
        const stop2 = new Set(stopAt);
        if (merge) stop2.add(merge);

        const snap = new Set(visited);
        const yesConn = step.connections.find(
          (c) => c.from === id && c.branch === "yes",
        );
        const noConn = step.connections.find(
          (c) => c.from === id && c.branch === "no",
        );
        if (yesConn) processChain(yesConn.to, action.yesLine!, stop2);
        const afterYes = new Set(visited);
        visited.clear();
        snap.forEach((x) => visited.add(x));
        if (noConn) processChain(noConn.to, action.noLine!, stop2);
        afterYes.forEach((x) => visited.add(x));

        if (merge) {
          visited.delete(merge);
          id = merge;
          continue;
        }
        break;
      }

      if (prim.type === "loop") {
        const stop2 = new Set(stopAt);
        const loopY = originalPositions?.get(id)?.y ?? prim.y;
        const connsFromLoop = step.connections.filter(
          (c) => c.from === id && !c.branch,
        );
        for (const bc of connsFromLoop) {
          const targetY =
            originalPositions?.get(bc.to)?.y ??
            step.primitives.find((p) => p.id === bc.to)?.y ??
            0;
          if (targetY > loopY + 20) {
            processChain(bc.to, action.bodyLine!, stop2);
          }
        }
        const afterConn = connsFromLoop.find((c) => {
          const targetY =
            originalPositions?.get(c.to)?.y ??
            step.primitives.find((p) => p.id === c.to)?.y ??
            0;
          return targetY <= loopY + 20;
        });
        if (afterConn) {
          visited.delete(afterConn.to);
          id = afterConn.to;
          continue;
        }
        break;
      }

      const next = step.connections.find((c) => c.from === id && !c.branch);
      id = next?.to ?? null;
    }
  }

  function findMergeByConnections(step: Step, startId: string): string | null {
    const prim = step.primitives.find((p) => p.id === startId);

    if (prim?.type === "loop") {
      return null;
    }
    if (prim?.type === "condition") {
      const directConn = step.connections.find(
        (c) => c.from === startId && !c.branch,
      );
      if (directConn) return directConn.to;

      const yesConn = step.connections.find(
        (c) => c.from === startId && c.branch === "yes",
      );
      const noConn = step.connections.find(
        (c) => c.from === startId && c.branch === "no",
      );
      if (!yesConn || !noConn) return null;

      const reachYes = reachableSet(step.connections, yesConn.to);
      const reachNo = reachableSet(step.connections, noConn.to);
      for (const id of reachYes) {
        if (reachNo.has(id)) return id;
      }
    }

    return null;
  }

  function reachableSet(
    connections: Step["connections"],
    startId: string | null,
  ): Set<string> {
    const visited = new Set<string>();
    const queue = startId ? [startId] : [];
    while (queue.length) {
      const cur = queue.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      connections
        .filter((c) => c.from === cur)
        .forEach((c) => queue.push(c.to));
    }
    return visited;
  }

  const firstConn = step.connections.find((c) => c.from === step.event?.id);
  if (firstConn) processChain(firstConn.to, actions, new Set());
  return actions;
}
